// The detail page's one loud element: where the request is, and who moved it
// there. Four real steps, so the strip is a sequence and reads as one.

import React from 'react';
import StepStrip from '../components/ui/StepStrip';
import { formatTimestamp } from '../utils/date-utils';
import type { RequisitionDetail } from './requisitionsApi';

type StepState = 'done' | 'current' | 'future' | 'stopped';

interface Step {
  label: string;
  state: StepState;
  detail: string | null;
}

const when = (ts: string | null) => (ts ? formatTimestamp(ts, 'MMM D') : '');

const byOn = (name: string | undefined | null, ts: string | null) =>
  [name, when(ts)].filter(Boolean).join(', ') || null;

/** How many steps are behind the request, and where it stopped if it did. */
function progress(r: RequisitionDetail): { done: number; stoppedAt: number | null } {
  switch (r.status) {
    case 'draft':
      return { done: 0, stoppedAt: null };
    case 'pending_approval':
      return { done: 1, stoppedAt: null };
    case 'approved':
      return { done: 2, stoppedAt: null };
    case 'bought':
      return { done: 3, stoppedAt: null };
    case 'arrived':
      return { done: 4, stoppedAt: null };
    case 'closed':
      return { done: 2, stoppedAt: 2 };
    case 'cancelled': {
      const done = r.approved_at ? 2 : r.submitted_at ? 1 : 0;
      return { done, stoppedAt: done };
    }
    default:
      return { done: 0, stoppedAt: null };
  }
}

export function buildSteps(r: RequisitionDetail): Step[] {
  const { done, stoppedAt } = progress(r);
  const stateAt = (i: number): StepState => {
    if (i < done) return 'done';
    if (stoppedAt === i) return 'stopped';
    if (stoppedAt !== null) return 'future';
    return i === done ? 'current' : 'future';
  };

  const stopWord = r.status === 'closed' ? "Won't buy" : 'Cancelled';
  const steps: Step[] = [
    {
      label: 'Asked',
      state: stateAt(0),
      detail:
        stateAt(0) === 'done'
          ? byOn(r.requester?.name, r.submitted_at)
          : stateAt(0) === 'current'
            ? 'Draft, not sent yet'
            : null,
    },
    {
      label: 'Approved',
      state: stateAt(1),
      detail:
        stateAt(1) === 'done'
          ? byOn(r.approved_by?.name, r.approved_at)
          : stateAt(1) === 'current'
            ? 'With the approver'
            : null,
    },
    {
      label: 'Bought',
      state: stateAt(2),
      detail:
        stateAt(2) === 'done'
          ? [byOn(r.bought_by?.name, r.bought_at), r.receipt_to_follow ? 'receipt to follow' : null]
              .filter(Boolean)
              .join(', ')
          : stateAt(2) === 'current'
            ? 'Waiting for someone to buy it'
            : null,
    },
    {
      label: 'Arrived',
      state: stateAt(3),
      detail:
        stateAt(3) === 'done'
          ? byOn(r.arrived_by?.name, r.arrived_at)
          : stateAt(3) === 'current'
            ? 'On the way'
            : null,
    },
  ];
  if (stoppedAt !== null) {
    steps[stoppedAt] = {
      ...steps[stoppedAt],
      label: stopWord,
      detail: r.status === 'closed' ? r.close_reason : null,
    };
  }
  return steps;
}

const StepsStrip: React.FC<{ requisition: RequisitionDetail }> = ({ requisition }) => (
  <StepStrip label="Progress" steps={buildSteps(requisition)} />
);

export default StepsStrip;
